/**
 * Media variants - the right pixels for the size being drawn.
 *
 * A card drawn at 170 px decoding a 1080×1350 original costs ~6 MB of RAM;
 * the same card from a 540 px variant costs under 1.5 MB. Ingest writes
 * variants onto the media item (`variants.thumb`) as it mints them; any item
 * without one simply draws the original, so a client that reads variants is
 * safe against every post that predates them.
 *
 * NORTHSTARS:
 * - The media item is canonical; the variant is a field on it, not a URL trick
 * - Fallback is the original — nothing ever breaks for lack of a variant
 * - Pure
 */

export interface MediaVariant {
  cdn_url?: string;
  width?: number;
  height?: number;
}

export interface MediaVariants {
  /** Card-sized: long edge ≈ 540 px, enough for a 180 px card on a 3× screen. */
  thumb?: MediaVariant;
}

export interface MediaItemWithVariants {
  cdn_url?: string;
  variants?: MediaVariants;
}

export interface CardMediaCandidate extends MediaItemWithVariants {
  type?: string;
  is_thumbnail?: boolean;
  is_cover?: boolean;
}

/**
 * The image that stands for a post when it is drawn small — the card, and the
 * poster under a video's frame. Priority: the generated thumbnail, then the
 * cover, then any image. Only images, only ones with a CDN URL.
 */
export const pickCardMediaItem = <T extends CardMediaCandidate>(items: readonly T[] | null | undefined): T | undefined => {
  if (!items || items.length === 0) return undefined;
  const images = items.filter(item => item && item.type === "image");
  return (
    images.find(item => item.is_thumbnail === true && isHttpUrl(item.cdn_url)) ??
    images.find(item => item.is_cover === true && isHttpUrl(item.cdn_url)) ??
    images.find(item => isHttpUrl(item.cdn_url))
  );
};

const isHttpUrl = (value: unknown): value is string =>
  typeof value === "string" && /^https?:\/\//.test(value);

/** The URL to draw this item small: its thumb variant when it has one, else the original. */
export const cardImageUrl = (item: MediaItemWithVariants | null | undefined): string | undefined => {
  if (!item) return undefined;
  const thumb = item.variants?.thumb?.cdn_url;
  if (isHttpUrl(thumb)) return thumb;
  return isHttpUrl(item.cdn_url) ? item.cdn_url : undefined;
};

/**
 * What a detail frame shows before its own media has painted: an image's
 * thumb variant (already on screen as the card, already in cache), a video's
 * card image. Nothing when there is nothing — the frame keeps its colour.
 */
export const framePoster = (
  item: (CardMediaCandidate & { type?: string }) | null | undefined,
  all: readonly CardMediaCandidate[] | null | undefined,
): string | undefined => {
  if (!item) return undefined;
  if (item.type === "video") return cardImageUrl(pickCardMediaItem(all));
  const thumb = item.variants?.thumb?.cdn_url;
  return isHttpUrl(thumb) ? thumb : undefined;
};

/**
 * The original to warm before the detail opens, when the card drew a variant
 * — so the full-size image is already in the browser cache by the time the
 * detail asks for it, as it was when cards drew originals. Nothing to warm
 * when the card already drew the original.
 */
export const originalToPrefetch = (item: MediaItemWithVariants | null | undefined): string | undefined => {
  if (!item) return undefined;
  const drawn = cardImageUrl(item);
  const original = isHttpUrl(item.cdn_url) ? item.cdn_url : undefined;
  return original && original !== drawn ? original : undefined;
};
